import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { parseISO } from 'date-fns';
import qs from 'qs';

import { API_URL, get } from 'lib/network/api';
import CardContentPackage from './ComponentsPage/CardContentPackage';
import Tabs from 'components/Tabs';

const fetchMyPackage = async () => {
  const queryString = qs.stringify({ category: 'class' });
  return get(`${API_URL}/api/user/membership?${queryString}`);
};

const MyPackage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') || 'active';

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
    const newStatus = tabId === 0 ? 'active' : 'expired';
    setSearchParams({ status: newStatus });
  };

  useEffect(() => {
    if (status === 'active') setActiveTab(0);
    if (status === 'expired' || status === 'inactive') setActiveTab(1);
  }, [status]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['myPackage'],
    queryFn: fetchMyPackage,
  });

  const filterPackagesByStatus = (packages, isActive) => {
    if (!packages) return [];

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    return packages.filter((pkg) => {
      try {
        if (!pkg.active_until) {
          return isActive;
        }

        const isUnlimited =
          typeof pkg.active_until === 'string' && pkg.active_until.startsWith('0001-01-01');
        if (isUnlimited) {
          return isActive;
        }

        const activeUntil = parseISO(pkg.active_until);
        if (Number.isNaN(activeUntil.getTime())) {
          return isActive;
        }

        activeUntil.setHours(0, 0, 0, 0);
        const isExpired = activeUntil < now;

        return isActive ? !isExpired : isExpired;
      } catch (error) {
        console.error('Error parsing active_until date:', error);
        return isActive;
      }
    });
  };

  const sortPackages = (packages) => {
    if (!packages) return [];

    return [...packages].sort((a, b) => {
      const aRemainingQuota = a.class_quota || 0;
      const bRemainingQuota = b.class_quota || 0;

      const normalizeExpiry = (dateStr) => {
        const isUnlimited = typeof dateStr === 'string' && dateStr.startsWith('0001-01-01');
        if (!dateStr || isUnlimited) return Number.MAX_SAFE_INTEGER;
        try {
          return parseISO(dateStr).getTime();
        } catch (err) {
          return Number.MAX_SAFE_INTEGER;
        }
      };

      if (aRemainingQuota > 0 && bRemainingQuota === 0) return -1;
      if (aRemainingQuota === 0 && bRemainingQuota > 0) return 1;

      const aExpiry = normalizeExpiry(a.active_until);
      const bExpiry = normalizeExpiry(b.active_until);
      return aExpiry - bExpiry;
    });
  };

  const activePackages = sortPackages(filterPackagesByStatus(data, true));
  const expiredPackages = sortPackages(filterPackagesByStatus(data, false));

  const tabs = [
    {
      id: 0,
      name: 'Active',
      content: <CardContentPackage data={activePackages} isLoading={isLoading} isError={isError} />,
    },
    {
      id: 1,
      name: 'Expired',
      content: (
        <CardContentPackage data={expiredPackages} isLoading={isLoading} isError={isError} />
      ),
    },
  ];

  return (
    <div>
      <h2 className="text-lg sm:text-xl font-semibold">My Class Passes</h2>
      <Tabs tabs={tabs} activeTab={activeTab} handleTabClick={handleTabClick} />
    </div>
  );
};

export default MyPackage;
